import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ENVIRONMENT_APP_NAME } from '@qtek/core/api-core';
import { AuthUserStore } from '@qtek/libs/auth-user';
import { LoginModel } from '@qtek/shared/models';
import {
  IMPERSONATION_EMAIL_KEY,
  IMPERSONATION_TOKEN_KEY,
  setImpersonationDomState,
} from '@qtek/shared/utils';
import { EMPTY, Observable, of, pipe, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  finalize,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';

import { CoreStore } from '../core/core.store';
import { UserStore } from '../user/user.store';
import { AuthService } from './auth.service';

export interface LogoutPayload {
  queryParams?: any;
  skipRedirect?: true;
}

export interface AuthState {
  isLoading: boolean;
  wsInitToken: string | null;
}

const initialState: AuthState = {
  isLoading: false,
  wsInitToken: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    loginError$: new Subject<void>(),
    loggedOut$: new Subject<void>(),
    resetPasswordEmailSent$: new Subject<void>(),
  })),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      router = inject(Router),
      route = inject(ActivatedRoute),
      matDialog = inject(MatDialog),
      document = inject(DOCUMENT),
      envAppName = inject(ENVIRONMENT_APP_NAME),
      coreStore = inject(CoreStore),
      userStore = inject(UserStore)
    ) => ({
      getWsInitToken(): Observable<string | null> {
        patchState(store, { wsInitToken: null });
        return authService.getWsSessionInitToken().pipe(
          map(({ res }) => res.wsTkn),
          catchError(() => of(null)),
          tap(wsInitToken => patchState(store, { wsInitToken }))
        );
      },

      setWsInitToken(wsInitToken: string): void {
        patchState(store, { wsInitToken });
      },

      login: rxMethod<LoginModel>(
        pipe(
          tap(() => {
            patchState(store, { isLoading: true });
            coreStore.openLoading();
          }),
          mergeMap(payload =>
            authService.login(payload).pipe(
              tap(res => {
                if (
                  res.sts?.sts === 'WARN' &&
                  (!res.res.company || !res.res.person)
                ) {
                  router.navigate(['/']);
                  return;
                }
                let { t } = route.snapshot.queryParams;
                const { returnUrl } = route.snapshot.queryParams;
                if (t && typeof t === 'string') {
                  const decodeMap: any = { '-': '+', _: '/', '.': '=' };
                  t = t.replace(/[-_.]/g, ch => decodeMap[ch]);
                  router.navigateByUrl(decodeURIComponent(atob(t)));
                  return;
                }
                router.navigateByUrl(returnUrl || '');
              }),
              tap(res => {
                if (
                  res.sts?.sts !== 'WARN' &&
                  res.res.company &&
                  res.res.person
                ) {
                  userStore.setUserData(res.res);
                }
                patchState(store, { isLoading: false });
              }),
              catchError(() => {
                coreStore.closeLoading();
                patchState(store, { isLoading: false });
                store.loginError$.next();
                return EMPTY;
              })
            )
          )
        )
      ),

      logout: rxMethod<LogoutPayload>(
        pipe(
          tap(() => {
            store.loggedOut$.next();
          }),
          concatMap(({ queryParams, skipRedirect }) =>
            authService.logout().pipe(
              finalize(() => {
                document.defaultView?.sessionStorage?.removeItem(
                  IMPERSONATION_TOKEN_KEY
                );
                document.defaultView?.sessionStorage?.removeItem(
                  IMPERSONATION_EMAIL_KEY
                );
                setImpersonationDomState(document, false);
                matDialog.closeAll();

                if (!skipRedirect) {
                  router.navigate([envAppName === 'booking' ? '' : 'auth'], {
                    queryParams: queryParams || null,
                    queryParamsHandling: 'merge',
                  });
                }
              }),
              catchError(() => EMPTY)
            )
          )
        )
      ),

      resetPasswordEmail: rxMethod<any>(
        pipe(
          switchMap(payload =>
            authService.sendResetPasswordEmail(payload).pipe(
              tap(() => store.resetPasswordEmailSent$.next()),
              catchError(() => EMPTY)
            )
          )
        )
      ),
    })
  ),
  withHooks((store, authUserStore = inject(AuthUserStore)) => ({
    onInit() {
      authUserStore.loggedOut$.subscribe(() => store.loggedOut$.next());
    },
  }))
);
